import * as React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import moment from 'moment';
import ActivityCard from 'components/Overview/ActivityCard';
import UserSearch from 'components/UserSearch';
import TitleWrapper from 'components/Overview/TitleWrapper';
import QuickOverview from 'components/Overview/QuickOverview';
import { Theme } from 'helpers/theme';
import ProfileCard from 'components/Overview/ProfileCard';
import PageHeader from 'components/PageHeader';
import { fetchQuery } from 'relay-runtime';
import { createFragmentContainer, graphql } from 'react-relay';
import { OrgOverview_manager } from './__generated__/OrgOverview_manager.graphql';
import environment from 'api/environment';
import { OrgOverview_purchasedCourses } from './__generated__/OrgOverview_purchasedCourses.graphql';
import { useRouter } from 'found';
import StatusCard from 'components/Overview/StatusCard/StatusCard';

const useStyles = createUseStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1
  },
  activity: {},
  statsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap'
  },
  cardFlex: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  breaker: {
    width: theme.spacing(2)
  },
  searchRow: {
    display: 'flex',
    flexWrap: 'wrap',
    height: 40
  },
  bottomRow: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap'
  },
  spacer: {
    minWidth: 770
  },
  grid: {
    padding: '40px 60px',
    display: 'grid',
    gridTemplateRows: 'auto',
    gridTemplateColumns: 'repeat(11, 1fr)',
    gridGap: 38,
    gridTemplateAreas: `
      "profle profle profle profle profle profle profle actvty actvty actvty actvty"
    `,
    '@media (max-width: 1150px)': {
      gridTemplateAreas: `
      "traini"
      "profle"
      "actvty"
    `
    }
  },
  yourInfo: {
    gridArea: 'profle'
  },
  activityWrapper: {
    gridArea: 'actvty'
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'white',
    padding: 24,
    paddingRight: 60,
    paddingLeft: 60,
    borderBottom: '1px solid #EDEDED'
  }
}));

type Props = {
  manager: OrgOverview_manager;
  purchasedCourses: OrgOverview_purchasedCourses;
};

const OrgOverview = ({ manager, purchasedCourses }: Props) => {
  const theme = useTheme();
  const classes = useStyles({ theme });

  const { router } = useRouter();

  return (
    <div className={classes.root}>
      <div className={classes.pageHeader}>
        <PageHeader
          title={manager.company.name}
          subTitle="Organisation Overview"
        />
        <QuickOverview
          purchasedCourses={purchasedCourses?.edges?.length ?? 0}
          numDelegates={manager.company.delegates?.pageInfo?.total ?? 0}
          numValidCertificates={
            manager.company.certificatesStatus?.totals?.available ?? 0
          }
          numCertificatesExpiringSoon={
            manager.company.certificatesStatus?.totals?.expiringSoon ?? 0
          }
        />
      </div>

      <div className={classes.grid}>
        <TitleWrapper title="Delegates Trained" className={classes.yourInfo}>
          <StatusCard company={manager.company} />
        </TitleWrapper>
        <TitleWrapper title={'Activity'} className={classes.activityWrapper}>
          <ActivityCard
            className={classes.activity}
            padding={'medium'}
            leftHeading={'Certificate status'}
            rightHeading={'Recent Updates'}
            options={[]}
            onClick={() => router.push('/app/delegates')}
            activity={manager.company.activity}
            data={{
              outerRing: {
                name: 'Current',
                value:
                  manager.company.certificatesStatus?.totals?.available ?? 0
              },
              innerRing: {
                name: 'Expired',
                value: manager.company.certificatesStatus?.totals?.expired ?? 0
              }
            }}
          />
        </TitleWrapper>
      </div>
    </div>
  );
};

export default createFragmentContainer(OrgOverview, {
  manager: graphql`
    fragment OrgOverview_manager on Manager {
      firstName
      lastName
      email
      telephone
      lastLogin
      company {
        name
        delegates {
          pageInfo {
            total
          }
        }
        certificatesStatus {
          totals {
            available
            expired
            expiringSoon
            incomplete
          }
        }
        activity(page: { limit: 6 }) {
          ...ActivityCard_activity
        }
        ...StatusCard_company
      }
    }
  `,
  purchasedCourses: graphql`
    fragment OrgOverview_purchasedCourses on PurchasedCoursePage {
      edges {
        counts {
          expiringTakers
        }
      }
    }
  `
});
